import React from 'react';

export const VOUCHER_ITEMS_PER_PAGE = 7;

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return String(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const chunkItems = (items, size) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return [[]];
  const pages = [];
  for (let i = 0; i < list.length; i += size) {
    pages.push(list.slice(i, i + size));
  }
  return pages;
};

/**
 * A5 landscape payment voucher for html-to-image → Electron print.
 * Renders one page per VOUCHER_ITEMS_PER_PAGE items; each page keeps id="expenseVoucherPage-{n}".
 */
const ExpenseVoucher = ({
  generalSetting,
  username,
  entryDate,
  paymentMode,
  expenseItems = [],
  grandTotal = 0,
  voucherNo = '',
}) => {
  const firmName = `${generalSetting?.FirstName || ''} ${generalSetting?.LastName || ''}`.trim();
  const mobiles = [generalSetting?.MobileNo1, generalSetting?.MobileNo2].filter(Boolean).join(' , ');
  const pages = chunkItems(expenseItems, VOUCHER_ITEMS_PER_PAGE);
  const totalPages = pages.length;

  return (
    <div className="p-8 bg-gray-500 flex flex-col gap-6">
      {pages.map((pageItems, pageIndex) => {
        const isLastPage = pageIndex === totalPages - 1;
        const startNo = pageIndex * VOUCHER_ITEMS_PER_PAGE;

        return (
          <div
            key={`voucher-page-${pageIndex}`}
            id={`expenseVoucherPage-${pageIndex}`}
            data-voucher-page={pageIndex}
            style={{ page: 'A5' }}
            className="h-[559px] w-[793px] bg-white flex flex-col text-black shrink-0"
          >
            <div className="px-5 pt-5 pb-3 flex-grow flex flex-col">
              <div className="w-full flex items-start justify-between gap-3">
                <div>
                  <p className="text-xl font-bold tracking-wide uppercase leading-tight">
                    {firmName || 'Payment Voucher'}
                  </p>
                  {mobiles ? (
                    <p className="text-sm text-black mt-1">{mobiles}</p>
                  ) : null}
                </div>
                {totalPages > 1 ? (
                  <p className="text-xs font-medium text-black whitespace-nowrap pt-1">
                    Page {pageIndex + 1} of {totalPages}
                  </p>
                ) : null}
              </div>

              <div className="mt-4 mb-3 flex items-center gap-3">
                <div className="flex-1 h-px bg-black" />
                <p className="text-lg font-bold tracking-[0.12em] uppercase whitespace-nowrap px-2">
                  Payment Voucher
                </p>
                <div className="flex-1 h-px bg-black" />
              </div>

              <div className="border border-black rounded-sm px-3 py-2 mb-3">
                <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                  <div className="flex gap-2">
                    <span className="w-28 shrink-0 text-black">Voucher No</span>
                    <span className="font-semibold">: {voucherNo || '—'}</span>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <span className="w-24 text-right shrink-0 text-black">Date</span>
                    <span className="font-semibold min-w-[110px]">: {formatDisplayDate(entryDate)}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="w-28 shrink-0 text-black">Payment Mode</span>
                    <span className="font-semibold">: {paymentMode || 'Cash'}</span>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <span className="w-24 text-right shrink-0 text-black">Prepared By</span>
                    <span className="font-semibold min-w-[110px]">: {username || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="border border-black flex-grow flex flex-col min-h-0">
                <div className="flex border-b border-black bg-white">
                  <p className="text-xs font-bold uppercase px-2 py-1.5 border-r border-black" style={{ width: '8%' }}>
                    No.
                  </p>
                  <p className="text-xs font-bold uppercase px-2 py-1.5 border-r border-black" style={{ width: '28%' }}>
                    Category
                  </p>
                  <p className="text-xs font-bold uppercase px-2 py-1.5 border-r border-black" style={{ width: '42%' }}>
                    Description
                  </p>
                  <p className="text-xs font-bold uppercase pr-3 pl-2 py-1.5 text-right" style={{ width: '22%' }}>
                    Amount
                  </p>
                </div>

                <div className="flex-grow">
                  {pageItems.length === 0 ? (
                    <p className="text-sm text-gray-500 px-2 py-3">No expense items</p>
                  ) : (
                    pageItems.map((item, index) => (
                      <div
                        key={`${item.category}-${startNo + index}`}
                        className="flex border-b border-black/40 last:border-b-0"
                      >
                        <p className="text-sm px-2 py-1.5 border-r border-black/40" style={{ width: '8%' }}>
                          {startNo + index + 1}
                        </p>
                        <p className="text-sm font-semibold px-2 py-1.5 border-r border-black/40" style={{ width: '28%' }}>
                          {item.category || '—'}
                        </p>
                        <p className="text-sm px-2 py-1.5 border-r border-black/40" style={{ width: '42%' }}>
                          {item.description || '—'}
                        </p>
                        <p className="text-sm font-semibold pr-3 pl-2 py-1.5 text-right tabular-nums" style={{ width: '22%' }}>
                          ₹{formatMoney(item.amount)}
                        </p>
                      </div>
                    ))
                  )}
                </div>

                <div className="flex border-t-2 border-black mt-auto">
                  {isLastPage ? (
                    <>
                      <p className="text-sm font-bold px-2 py-2 text-right border-r border-black" style={{ width: '78%' }}>
                        Total Amount
                      </p>
                      <p className="text-sm font-bold pr-3 pl-2 py-2 text-right tabular-nums" style={{ width: '22%' }}>
                        ₹{formatMoney(grandTotal)}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm font-medium px-2 py-2 text-right w-full italic">
                      Continued on next page…
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="px-5 pb-5 flex justify-end">
              {isLastPage ? (
                <div className="w-44 text-center pt-10">
                  <div className="border-t border-black w-full" />
                  <p className="text-xs mt-1.5 font-medium">Authorized Signature</p>
                </div>
              ) : (
                <div className="w-44 h-[52px]" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExpenseVoucher;
